import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Ship, Anchor, Globe, Snowflake, Building2, Database, Filter } from "lucide-react";

const EUETSCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const emissionCalculations = [
    {
      id: "emission-factor-sourcing",
      title: "Emission Factor Sourcing",
      description: "Retrieves emission factors from FuelTable database for HFO, LFO, MGO",
      formula: "CF_CO2, CF_CH4, CF_N20, CSlip from FuelTable collection",
      subFormulas: [
        "FuelTable.Fuel_Type must include: HFO, LFO, MGO",
        "Required fields: CF_C02, CF_CH4, CF_N20, CSlip",
        "Validates all three fuel types exist before calculation",
        "Database values override any hardcoded defaults"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "co2-calculation",
      title: "CO₂ Emission Calculation",
      description: "Calculates CO₂ emissions for each fuel type (no CSlip adjustment)",
      formula: "CO₂ (MT) = Fuel Consumption (MT) × CF_CO₂",
      subFormulas: [
        "CO₂_HFO = HFO × CF_CO₂_HFO",
        "CO₂_LFO = LFO × CF_CO₂_LFO",
        "CO₂_MGO = MGO × CF_CO₂_MGO",
        "Total CO₂ = CO₂_HFO + CO₂_LFO + CO₂_MGO"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "ch4-calculation-with-cslip",
      title: "CH₄ Emission Calculation (with CSlip)",
      description: "Calculates CH₄ emissions including Carbon Slip adjustment",
      formula: "CH₄ (MT) = Fuel × CF_CH₄ + (Fuel × CSlip%)",
      subFormulas: [
        "CH₄_HFO = HFO × CF_CH₄_HFO + (HFO × CSlip_HFO%)",
        "CH₄_LFO = LFO × CF_CH₄_LFO + (LFO × CSlip_LFO%)",
        "CH₄_MGO = MGO × CF_CH₄_MGO + (MGO × CSlip_MGO%)",
        "CSlip values from FuelTable (percentage)"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "n2o-calculation",
      title: "N₂O Emission Calculation",
      description: "Calculates N₂O emissions for each fuel type (no CSlip adjustment)",
      formula: "N₂O (MT) = Fuel Consumption (MT) × CF_N₂O",
      subFormulas: [
        "N₂O_HFO = HFO × CF_N₂O_HFO",
        "N₂O_LFO = LFO × CF_N₂O_LFO",
        "N₂O_MGO = MGO × CF_N₂O_MGO",
        "Total N₂O = N₂O_HFO + N₂O_LFO + N₂O_MGO"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "ghg-calculation",
      title: "Total GHG Calculation",
      description: "Calculates total greenhouse gas emissions using Global Warming Potentials",
      formula: "GHG (MT CO₂e) = CO₂ + (CH₄ × 28) + (N₂O × 265)",
      subFormulas: [
        "GWP_CH4 = 28 (28× CO₂ equivalent)",
        "GWP_N2O = 265 (265× CO₂ equivalent)",
        "GHG = Total CO₂ + (Total CH₄ × 28) + (Total N₂O × 265)",
        "Units: Metric Tons CO₂ equivalent"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    }
  ];

  const segmentClassification = [
    {
      id: "vessel-state-detection",
      title: "Vessel State Detection",
      description: "Determines vessel operational state based on vesselStateTransition",
      formula: "State = Analyze vesselStateTransition field",
      subFormulas: [
        "At Sea States: 'At Sea', 'At Sea (Drifting)', 'Maneuvering'",
        "In Port States: 'In Port', 'Maneuvering (anchored)'",
        "Unknown States: Fallback to port-based classification",
        "Fuel allocation based on detected state"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "port-classification",
      title: "Port Classification",
      description: "Classifies ports as EEA or non-EEA using Port collection",
      formula: "EEA Port = Port.eea === true",
      subFormulas: [
        "Load all ports from Port collection",
        "Create Set of normalized port names where eea = true",
        "Also includes portCode if present",
        "Transhipment ports: 'Port Said East', 'Tanger Med' (container ships only)"
      ],
      icon: <Anchor className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "segment-identification",
      title: "Segment Identification",
      description: "Classifies voyages into EU ETS segments based on port pairs",
      formula: "Segment = classifySegment(fromPort, toPort, euSet, isContainerVessel)",
      subFormulas: [
        "between: Both ports are EEA (or transhipment for containers)",
        "departedEEAtoNonEEA: EEA → Non-EEA",
        "departedNonEEAtoEEA: Non-EEA → EEA",
        "ports: At EEA port (vesselState = 'In Port')"
      ],
      icon: <Globe className="w-5 h-5" />,
      borderColor: "border-yellow-200",
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-700",
    },
    {
      id: "container-vessel-logic",
      title: "Container Vessel Special Logic",
      description: "Special handling for container ships including transhipment ports",
      formula: "isContainerVessel = vesselType === 'Container ships'",
      subFormulas: [
        "Additional segments: nonEEAToTranshipment, transhipmentToEEA, etc.",
        "Transhipment ports treated as EEA for container vessels",
        "Different ETS percentage calculations",
        "Enhanced voyage classification"
      ],
      icon: <Ship className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    }
  ];

  const complianceCalculations = [
    {
      id: "ets-percentage",
      title: "ETS Coverage Percentage",
      description: "Determines percentage of emissions covered by EU ETS",
      formula: "ETS % = getETSPercentage(segment, fromPort, toPort)",
      subFormulas: [
        "ports, between: 100% coverage (1.0)",
        "departed/arrived segments: 50% coverage (0.5)",
        "Container vessels: special transhipment logic",
        "Transhipment segments: 50% coverage"
      ],
      icon: <Calculator className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "reduction-factor",
      title: "Reduction Factor",
      description: "Annual phase-in factor from ReductionFactor collection",
      formula: "Phase = ReductionFactor.findOne({year})",
      subFormulas: [
        "2024: 0.4 (40% of emissions covered)",
        "2025: 0.7 (70% of emissions covered)",
        "Default: 1.0 (100% if year not found)",
        "Stored in ReductionFactor collection"
      ],
      icon: <Database className="w-5 h-5" />,
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "ice-class-exemption",
      title: "Ice Class Exemption",
      description: "5% discount for qualifying ice-class vessels",
      formula: "Ice Factor = isIceClassExempt ? 0.95 : 1.0",
      subFormulas: [
        "Requires: iceClassShip = 'YES' AND iceClassType in exempt list",
        "Exempt types: IA Super, IA, IB, IC, Arc 4-9",
        "5% discount = 0.95 multiplier",
        "Applied to final EUA calculation"
      ],
      icon: <Snowflake className="w-5 h-5" />,
      borderColor: "border-cyan-200",
      bgColor: "bg-cyan-50",
      textColor: "text-cyan-700",
    },
    {
      id: "eua-calculation",
      title: "EU Allowance (EUA) Calculation",
      description: "Final EU Allowances required for compliance",
      formula: "Final EUAs = GHG_Under_ETS × Reduction_Factor × Ice_Factor",
      subFormulas: [
        "GHG_Under_ETS = Segment_GHG × ETS_%",
        "Base_EUAs = GHG_Under_ETS × Reduction_Factor",
        "Final_EUAs = Base_EUAs × Ice_Factor (0.95 if exempt)",
        "Units: Metric Tons CO₂ equivalent"
      ],
      icon: <Building2 className="w-5 h-5" />,
      borderColor: "border-gray-200",
      bgColor: "bg-gray-50",
      textColor: "text-gray-700",
    }
  ];

  const dataSources = [
    {
      collection: "FuelTable",
      purpose: "Emission Factors",
      fields: "CF_C02, CF_CH4, CF_N20, CSlip, Fuel_Type",
      required: ["HFO", "LFO", "MGO"]
    },
    {
      collection: "ReductionFactor",
      purpose: "Phase-in Factors",
      fields: "year, reductionFactor",
      examples: "2024: 0.4, 2025: 0.7"
    },
    {
      collection: "Port",
      purpose: "EEA Port Identification",
      fields: "portName, portCode, eea (boolean)",
      note: "Normalized to lowercase for comparison"
    },
    {
      collection: "VesselVoyage",
      purpose: "Voyage Data",
      fields: "fromPort, toPort, hfo, lfo, mgo, vesselStateTransition",
      note: "Fuel consumption and state tracking"
    },
    {
      collection: "Vessel",
      purpose: "Vessel Metadata",
      fields: "vesselType, iceClassShip, iceClassType",
      note: "Determines special handling rules"
    }
  ];

  const transhipmentPorts = ["Port Said East", "Tanger Med"];
  const iceClassExemptTypes = ["IA Super", "IA", "IB", "IC", "Arc 4", "Arc 5", "Arc 6", "Arc 7", "Arc 8", "Arc 9"];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Calculator className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                EU ETS Emissions Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Comprehensive EU Emissions Trading System compliance methodology
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Data Sources</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-2">{source.collection}</h4>
                    <div className="text-sm text-gray-600 mb-2">{source.purpose}</div>
                    <div className="text-xs text-gray-500 mb-2">Fields: {source.fields}</div>
                    {source.required && (
                      <div className="text-xs text-amber-600">Required: {source.required.join(", ")}</div>
                    )}
                    {source.examples && (
                      <div className="text-xs text-green-600 mt-1">{source.examples}</div>
                    )}
                    {source.note && (
                      <div className="text-xs text-gray-500 mt-1 italic">{source.note}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Special Rules & Parameters
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <Ship className="w-4 h-4" />
                    Container Vessel Transhipment Ports
                  </h4>
                  <p className="text-sm text-blue-700 mb-2">Treated as EEA ports for container vessels only:</p>
                  <ul className="text-sm text-blue-600 space-y-1">
                    {transhipmentPorts.map((port, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="text-blue-400">•</span>
                        {port}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-blue-500 mt-2">50% ETS coverage for transhipment segments</p>
                </div>
                <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                  <h4 className="font-semibold text-cyan-800 mb-2 flex items-center gap-2">
                    <Snowflake className="w-4 h-4" />
                    Ice Class Exemption Types
                  </h4>
                  <p className="text-sm text-cyan-700 mb-2">Qualifies for 5% EUA discount:</p>
                  <div className="grid grid-cols-2 gap-2">
                    {iceClassExemptTypes.map((type, idx) => (
                      <span key={idx} className="text-xs bg-white px-2 py-1 rounded border text-cyan-600">
                        {type}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-cyan-500 mt-2">Requires: iceClassShip = 'YES' AND matching iceClassType</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Emission Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {emissionCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Classification</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {segmentClassification.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Logic:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Compliance Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {complianceCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div>
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-4">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <p className="text-sm font-semibold bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </p>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-semibold text-amber-800 mb-2">Complete EUA Calculation</h4>
              <div className="text-sm text-amber-700 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 1: GHG Calculation</div>
                    <div className="text-xs font-mono mt-1">GHG = CO₂ + (CH₄ × 28) + (N₂O × 265)</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 2: ETS Coverage</div>
                    <div className="text-xs font-mono mt-1">GHG_Under_ETS = GHG × ETS_%</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium">Step 3: Final EUAs</div>
                    <div className="text-xs font-mono mt-1">Final_EUAs = GHG_Under_ETS × Reduction_Factor × Ice_Factor</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EUETSCalculations;